import fillStatisticsSheet from './fillStatisticsSheet'
import statisticsCoords from '../../constants/statisticsCoords.json'

// The pdf-lib side of the FIBA statistics sheet: loads the template, embeds the fonts, draws
// each field at the coordinates of statisticsCoords.json and hands the PDF to the browser.
// A port of msm-stats statisticsFunctions.ts (createDrawer, createFramer, generateStatisticsPdf).

const CELL_HEIGHT = 11
const CELL_FONT_SIZE = 8
const CELL_BASELINE_OFFSET = 8.6
const HEADER_BASELINE_RATIO = 0.78
// Shrink in 0.5 pt steps down to 4 pt so long names never overflow their box.
const MIN_FONT_SIZE = 4
const TEMPLATE_LINE_WIDTH = 0.25

// Standard fonts only cover WinAnsi; anything beyond Latin-1 (Greek names, Serbian diacritics)
// has to go through the embedded TTF.
export const isWinAnsiSafe = (text) => /^[ -ÿ–—‘’“”•…€]*$/.test(text)

const decodeBase64 = (base64) => Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))

// Mismatch logo, top-right, above the competition title (which starts 34.2 pt from the top), and
// right-aligned with the table's edge at 559 pt. Top-left origin, PDF points.
export const LOGO_BOX = { right: 559, top: 12, width: 80 }

/** Where the logo goes for an image of `imageWidth` x `imageHeight`, in pdf-lib's bottom-left origin. */
export const logoPlacement = (pageHeight, imageWidth, imageHeight) => {
  const height = (LOGO_BOX.width * imageHeight) / imageWidth
  return {
    x: LOGO_BOX.right - LOGO_BOX.width,
    y: pageHeight - LOGO_BOX.top - height,
    width: LOGO_BOX.width,
    height,
  }
}

const drawLogo = async (pdfDoc, page, logoBase64) => {
  try {
    const image = await pdfDoc.embedPng(decodeBase64(logoBase64))
    page.drawImage(image, logoPlacement(page.getHeight(), image.width, image.height))
  } catch (error) {
    // The sheet is still valid without the logo.
    console.warn('Statistics PDF: logo failed:', error)
  }
}

/**
 * Draws a box from the coordinates file with the template's 0.25 pt line, split into `rows`
 * equal rows. Only used for areas the template doesn't print, like the overtime columns.
 */
export const createFramer = (page, coords, rgb) => (key, rows = 1) => {
  const box = coords[key]
  if (!box) return
  const bottom = page.getHeight() - box.y - box.height
  const black = rgb(0, 0, 0)
  page.drawRectangle({
    x: box.x,
    y: bottom,
    width: box.width,
    height: box.height,
    borderColor: black,
    borderWidth: TEMPLATE_LINE_WIDTH,
  })
  for (let row = 1; row < rows; row += 1) {
    const y = bottom + (box.height * row) / rows
    page.drawLine({
      start: { x: box.x, y },
      end: { x: box.x + box.width, y },
      thickness: TEMPLATE_LINE_WIDTH,
      color: black,
    })
  }
}

/**
 * `draw(key, text, { align, bold })`: writes `text` inside the box named `key`, shrinking the
 * font until it fits. `fonts` is `{ regular, bold, unicode }`; bold text that is not WinAnsi
 * safe falls back to the unicode font. `color` is optional (tests pass none).
 */
export const createDrawer = (page, fonts, coords, color) => {
  const pageHeight = page.getHeight()

  return (key, text, options = {}) => {
    const value = (text ?? '').toString().trim()
    if (!value) return
    const box = coords[key]
    if (!box) {
      console.warn(`Statistics PDF: unknown field "${key}"`)
      return
    }

    const { x, y, width, height } = box
    const isCell = Math.abs(height - CELL_HEIGHT) < 0.5
    const bold = options.bold ?? false
    const align = options.align ?? 'center'

    const font = bold && isWinAnsiSafe(value) ? fonts.bold : bold ? fonts.unicode : fonts.regular

    let size = isCell ? CELL_FONT_SIZE : height
    let textWidth = font.widthOfTextAtSize(value, size)
    while (textWidth > width - 2 && size > MIN_FONT_SIZE) {
      size -= 0.5
      textWidth = font.widthOfTextAtSize(value, size)
    }

    const pad = isCell ? 1 : 0
    const startX =
      align === 'left' ? x + pad : align === 'right' ? x + width - pad - textWidth : x + (width - textWidth) / 2

    const baselineFromTop = isCell ? y + CELL_BASELINE_OFFSET : y + HEADER_BASELINE_RATIO * height

    page.drawText(value, { x: startX, y: pageHeight - baselineFromTop, size, font, color })
  }
}

export const fileNameOf = (response) => {
  const [home, away] = response?.stats?.teams || []
  const safe = (name) => (name || '').replace(/[\\/:*?"<>|]+/g, '').trim()
  return `${safe(home?.name)} - ${safe(away?.name)} - Statistics.pdf`
}

const loadFonts = async (pdfDoc, StandardFonts, robotoBase64) => {
  let unicode
  try {
    unicode = await pdfDoc.embedFont(decodeBase64(robotoBase64))
  } catch (error) {
    console.warn('Statistics PDF: Roboto failed, using Helvetica:', error)
    unicode = await pdfDoc.embedFont(StandardFonts.Helvetica)
  }
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
  return { regular: unicode, bold, unicode }
}

const saveToBrowser = (bytes, fileName) => {
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// pdf-lib, fontkit, the template and the font are a large download, so they load only when a
// sheet is asked for.
/** Builds the filled statistics sheet and returns the PDF bytes. */
export const buildStatisticsPdf = async (response) => {
  const [
    { PDFDocument, StandardFonts, rgb },
    { default: fontkit },
    { default: templateBase64 },
    { default: robotoBase64 },
    { default: logoBase64 },
  ] = await Promise.all([
    import('pdf-lib'),
    import('@pdf-lib/fontkit'),
    import('../../constants/statisticsTemplate.base64'),
    import('../../constants/robotoFont.base64'),
    import('../../constants/mismatchLogo.base64'),
  ])

  const pdfDoc = await PDFDocument.load(templateBase64)
  pdfDoc.registerFontkit(fontkit)
  const fonts = await loadFonts(pdfDoc, StandardFonts, robotoBase64)
  const page = pdfDoc.getPages()[0]
  await drawLogo(pdfDoc, page, logoBase64)

  const [home, away] = response?.stats?.teams || []
  pdfDoc.setTitle(`${home?.name || 'Home'} vs ${away?.name || 'Away'} - Statistics`.normalize('NFC'))
  pdfDoc.setSubject('Basketball Statistics')
  pdfDoc.setAuthor('Mismatch')
  pdfDoc.setCreator('Mismatch')

  fillStatisticsSheet(
    createDrawer(page, fonts, statisticsCoords, rgb(0, 0, 0)),
    response,
    createFramer(page, statisticsCoords, rgb),
  )

  return pdfDoc.save()
}

const downloadStatisticsPdf = async (response) => {
  saveToBrowser(await buildStatisticsPdf(response), fileNameOf(response))
}

export default downloadStatisticsPdf
